<template>
  <base-form-wrapper
    :id="formId"
    :title="$t('details.book-a-test-drive')"
    :icon="icon"
    altcolor
  >
    <form class="test-drive-form" @submit.prevent="sendForm">
      <div
        v-for="{
          name: fieldName,
          type,
          placeholder,
          maxLength,
          validations
        } in fields"
        :key="fieldName"
        class="test-drive-form__input"
      >
        <input
          v-model.trim="$v[fieldName].$model"
          :class="[
            'form-input',
            {
              'form-input--error': $v[fieldName].$error,
              'form-input--changed': $v[fieldName].$dirty
            }
          ]"
          :type="type || 'text'"
          :name="fieldName"
          :placeholder="placeholder"
          :maxlength="maxLength"
        />
        <base-validation-error
          v-for="(value, validationType) in validations"
          :key="validationType"
          :has-error="!$v[fieldName][validationType] && $v[fieldName].$error"
        >
          {{
            $t(`errors.${kebabCase(validationType)}`, {
              field: capitalize(fieldName)
            })
          }}
        </base-validation-error>
      </div>
      <div class="test-drive-form__input">
        <datepicker
          v-model="$v.date.$model"
          full-month-name
          :input-class="[
            'form-input',
            {
              'form-input--error': $v.date.$error,
              'form-input--changed': $v.date.$dirty
            }
          ]"
          :disabled-dates="{ to: new Date() }"
          :placeholder="$t('shared.date')"
        >
          <calendar-icon slot="afterDateInput" class="form-input__icon" />
        </datepicker>
        <base-validation-error :has-error="!$v.date.required && $v.date.$error">
          {{
            $t("errors.required", {
              field: $t("shared.date")
            })
          }}
        </base-validation-error>
      </div>
      <div class="test-drive-form__input">
        <base-select
          :placeholder="$t('shared.time')"
          :options="timeTreshholds"
          :error="$v.time.$error"
          class="form-select"
          :active="time"
          @update-store="time = $event"
        />
        <base-validation-error :has-error="!$v.time.required && $v.time.$error">
          {{
            $t("errors.required", {
              field: $t("shared.time")
            })
          }}
        </base-validation-error>
      </div>
      <div>
        <base-button
          altcolor
          :disabled="submitStatus === 'OK'"
          class="submit"
          type="submit"
        >
          {{ $t("details.book-a-test-drive") }}
        </base-button>
        <base-validation-error :has-error="submitStatus === 'ERROR'">
          {{ $t("errors.submit-error") }}
        </base-validation-error>
      </div>
      <base-overlay v-if="submitStatus === 'OK'">
        {{ $t("success.form-sent") }}
      </base-overlay>
    </form>
  </base-form-wrapper>
</template>

<script>
import { kebabCase } from "lodash";

import BaseFormWrapper from "@/components/ui/BaseFormWrapper/BaseFormWrapper.vue";
import BaseValidationError from "@/components/ui/BaseValidationError/BaseValidationError.vue";
import BaseSelect from "@/components/ui/BaseSelect/BaseSelect.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseOverlay from "@/components/ui/BaseOverlay/BaseOverlay.vue";
import Datepicker from "vuejs-datepicker";
import { validationMixin } from "vuelidate";
import { required, email, not } from "vuelidate/lib/validators";
import { usedCarsAPI, BOOK_TEST_DRIVE } from "@/api";
import { removeSpaces, formatDateForApi, capitalize } from "@/utils";
import { timeTreshholds } from "@/constants/testDriveForm";
import testDriveIcon from "@/assets/images/test-drive.svg";
import calendarIcon from "@/assets/images/calendar.svg";

const isMobileValid = value => {
  if (!value) return true;
  let min, max;
  const valueWithoutSpaces = removeSpaces(value);

  valueWithoutSpaces.substring(0, 1) == "+"
    ? ((min = 11), (max = 18))
    : ((min = 9), (max = 15));

  return (
    valueWithoutSpaces.length <= max &&
    valueWithoutSpaces.length >= min &&
    /^\+?[0-9]*$/.test(valueWithoutSpaces)
  );
};

export default {
  name: "TestDriveForm",
  components: {
    BaseFormWrapper,
    BaseValidationError,
    BaseSelect,
    Datepicker,
    calendarIcon,
    BaseButton,
    BaseOverlay
  },
  mixins: [validationMixin],
  props: {
    carId: {
      type: Number,
      required: true
    },
    formId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      name: null,
      mobile: null,
      email: null,
      date: null,
      time: null,
      submitStatus: null
    };
  },
  computed: {
    icon() {
      return testDriveIcon;
    },
    fields() {
      return [
        {
          name: "name",
          placeholder: this.$t("fields.name"),
          validations: {
            required: true,
            hasNumbers: true,
            hasSpecialCharacters: true
          }
        },
        {
          name: "mobile",
          placeholder: this.$t("fields.mobile"),
          validations: { required: true, invalid: true },
          type: "tel",
          maxLength: 18
        },
        {
          name: "email",
          placeholder: this.$t("fields.email"),
          validations: { required: true, invalid: true },
          type: "email"
        }
      ];
    },
    timeTreshholds() {
      return timeTreshholds;
    },
    capitalize() {
      return capitalize;
    },
    formatDatetime() {
      return `${formatDateForApi(this.date)} ${this.time}`;
    },
    kebabCase() {
      return kebabCase;
    }
  },
  methods: {
    sendForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.submitStatus = "ERROR";
      } else {
        this.submitStatus = "PENDING";
        usedCarsAPI()
          .post(BOOK_TEST_DRIVE(this.carId), {
            name: this.name,
            phone_number: removeSpaces(this.mobile),
            email: this.email,
            datetime: this.formatDatetime
          })
          .then(() => {
            this.submitStatus = "OK";
          })
          .catch(() => {
            this.submitStatus = "ERROR";
          });
      }
    }
  },
  validations: {
    name: {
      required,
      hasNumbers: not(value => {
        return value.match(/\d/);
      }),
      hasSpecialCharacters: not(value => {
        return value.match(/[!@#$%^&*(),.?":{}|<>/]/);
      })
    },
    mobile: {
      required,
      isMobileValid
    },
    email: {
      required,
      email
    },
    date: {
      required
    },
    time: {
      required
    }
  }
};
</script>

<style lang="scss">
.test-drive-form {
  position: relative;
}

.vdp-datepicker__calendar {
  z-index: $z-index-5 !important;
  width: 100% !important;
  padding: 10px 0;

  .day-header {
    text-transform: uppercase;
    border-bottom: 1px solid $color-grey-100;
    height: 50px;
    line-height: 50px;
    color: $color-grey-300;
  }

  .day.selected {
    position: relative;
    background: none !important;
    color: $color-white;

    &::before {
      @include absolute(top 50% left 50%);
      @include circle(30px);

      transform: translate(-50%, -50%);
      border: 1px solid transparent;
      border-radius: 100%;
      content: "";
      background-color: $color-blue-200;
      z-index: -1;
    }
  }
}

@media screen and (min-width: $media-l) {
  .test-drive-form {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;

    &__input {
      @include flex-basis(20%);

      padding: 0 5px;
      box-sizing: border-box;
    }
  }
}
</style>
